/**
 * useDatasets Hook
 *
 * React Query hook for managing dataset state.
 * Provides methods for listing datasets and fetching real-time statistics.
 */

import { useQuery } from '@tanstack/react-query';
import { datasetService } from '../services/datasetService';
import type {
  DatasetListResponse,
  DatasetFilters,
  DatasetStats,
} from '../services/types';
import { APIErrorException } from '../services/types/errors';

/**
 * Query key factory for dataset queries
 */
export const datasetKeys = {
  all: ['datasets'] as const,
  lists: () => [...datasetKeys.all, 'list'] as const,
  list: (filters?: DatasetFilters) => [...datasetKeys.lists(), filters] as const,
  details: () => [...datasetKeys.all, 'detail'] as const,
  detail: (datasetId: string) => [...datasetKeys.details(), datasetId] as const,
  stats: (datasetId: string) => [...datasetKeys.detail(datasetId), 'stats'] as const,
};

/**
 * Hook to fetch datasets with optional filters
 *
 * @param filters - Optional filters (type, status)
 * @returns React Query result with datasets
 */
export const useDatasets = (filters?: DatasetFilters) => {
  return useQuery<DatasetListResponse, Error>({
    queryKey: datasetKeys.list(filters),
    queryFn: async () => {
      return datasetService.list(filters);
    },
    retry: (failureCount, error) => {
      // Don't retry on 401 (unauthorized)
      if (error instanceof APIErrorException && error.status === 401) {
        return false;
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to fetch real-time statistics for a specific dataset
 *
 * Includes automatic polling every 30 seconds to keep stats fresh.
 *
 * @param datasetId - UUID of the dataset
 * @param options - Query options
 * @param options.enabled - Whether to enable the query (default: true)
 * @param options.pollingInterval - Polling interval in ms (default: 30000)
 * @returns React Query result with dataset stats
 */
export const useDatasetStats = (
  datasetId: string,
  options?: {
    enabled?: boolean;
    pollingInterval?: number;
  }
) => {
  const { enabled = true, pollingInterval = 30000 } = options || {};

  return useQuery<DatasetStats, Error>({
    queryKey: datasetKeys.stats(datasetId),
    queryFn: async () => {
      return datasetService.getStats(datasetId);
    },
    enabled: enabled && !!datasetId,
    retry: (failureCount, error) => {
      // Don't retry on 404 (dataset not found) or 401 (unauthorized)
      if (error instanceof APIErrorException) {
        if (error.status === 404 || error.status === 401) {
          return false;
        }
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    refetchInterval: pollingInterval, // Poll every 30 seconds by default
    staleTime: 10 * 1000, // Consider data stale after 10 seconds
    refetchOnWindowFocus: true,
  });
};
